import {useContext, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomPopover, {atomPopoverPositions} from '@s-ui/react-atom-popover'
import Context from '@s-ui/react-context'
import ActionsAdDiscard, {actionsAdDiscardTypes} from '@adv-mt/mt-actions-ad-discard'
import ActionsAdShare, {
  actionAdShareButtonSizes,
  actionAdShareTrackingOrigins,
  actionAdShareTypes
} from '@adv-mt/mt-actions-ad-share'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import CardAdFav from './card-ad-fav.js'
import CardAdMenu from './card-ad-menu.js'
import CardAdMenuIcon from './card-ad-menu-icon.js'

const CARD_ROLE = `card-ad`
const TITLE_TEST_ID = `${CARD_ROLE}-title`

const BASE_CLASS_INFO_HEADER = 'mt-CardAd-infoHeader'
const TITLE_CLASS = `${BASE_CLASS_INFO_HEADER}Title`
const ACTIONS_CLASS = `${BASE_CLASS_INFO_HEADER}Actions`

const CardAdInfoHeader = ({
  ad,
  cardAdFavProps,
  extendedTrackingData,
  hasMenu,
  isDiscardedAdsEnabled,
  isDeleted,
  isPreview,
  linkFactory: Link,
  onAfterDiscard = () => {},
  showFavorite,
  trackingData
}) => {
  const [isMakedAsFavorite, setIsMakedAsFavorite] = useState(ad.isFavorite)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const {domain} = useContext(Context)

  const {track} = useAnalyticsTrack()

  const delayToNextFrame = useDelayToNextFrame()

  const config = domain.get('config')

  const {HIT_ORIGINS, HIT_INFORMATIONS, TRACKING_EVENT_NAMES} = config.get('tracking')

  const {id, isSubscription: isSubscriptionAd, title, url, priceDrop} = ad

  const {onToggleFav: onToggleFavCb} = cardAdFavProps

  const showFavIcon = !isPreview && !isDeleted && showFavorite

  const favoriteActionProps = {
    ...cardAdFavProps,
    onToggleFav: nextFavoriteState => {
      setIsMakedAsFavorite(nextFavoriteState)
      onToggleFavCb && onToggleFavCb(nextFavoriteState)
    }
  }

  // TODO: commit talking about putting all cards in one line always
  const titleHeadingClass = cx(TITLE_CLASS, {
    [`${TITLE_CLASS}--oneLine`]: isSubscriptionAd || Boolean(priceDrop)
  })
  const priceDropActionProps = {
    onToggleFav: nextFavoriteState => {
      setIsMakedAsFavorite(nextFavoriteState)
      onToggleFavCb && onToggleFavCb(nextFavoriteState)
      setIsMenuOpen(false)
    }
  }

  const isMobile = config.get('isMobile')
  const {IS_PRICE_DROP_BUTTON_ENABLED} = config.get('favorites') || false

  const hasMenuItems =
    isMobile || isDiscardedAdsEnabled || (IS_PRICE_DROP_BUTTON_ENABLED && !isSubscriptionAd && !isMakedAsFavorite) // Temporary condition to avoid rendering the menu in desktop until we have more items, share modal rollout

  const showCardMenu = hasMenu && hasMenuItems

  const handleClickMenu = e => {
    e.stopPropagation()
    e.preventDefault()

    setIsMenuOpen(!isMenuOpen)

    delayToNextFrame(() => {
      track({
        event: TRACKING_EVENT_NAMES.BUTTON_CLICKED,
        data: {
          hit_origin: HIT_ORIGINS?.ADS_LIST_MENU,
          hit_information: HIT_INFORMATIONS.ADS_LIST_MENU_CLICKED
        }
      })
    })
  }

  const handleCloseMenu = () => {
    setIsMenuOpen(false)
  }

  const discardedAdsProps = {
    adId: id,
    isFavorite: isMakedAsFavorite,
    onAfterDiscard: () => {
      onAfterDiscard()
    },
    origin: HIT_ORIGINS?.ADS_LIST_MENU_AD_DISCARDED,
    size: 'large',
    type: actionsAdDiscardTypes.MENU_BUTTON
  }

  const companyUrl = config.get('COMPANY_URL')
  const referrer = `${companyUrl}${ad.url}`

  return (
    <div className={BASE_CLASS_INFO_HEADER}>
      <h2 className={titleHeadingClass} data-testid={TITLE_TEST_ID}>
        <Link className={`${TITLE_CLASS}Link`} href={url}>
          {title}
        </Link>
      </h2>
      <div className={ACTIONS_CLASS}>
        {isDiscardedAdsEnabled && !showCardMenu && (
          <ActionsAdDiscard
            adId={id}
            isFavorite={isMakedAsFavorite}
            onAfterDiscard={() => {
              onAfterDiscard()
            }}
            origin={HIT_ORIGINS?.AD_LIST_AD_DISCARDED}
          />
        )}
        {showFavIcon && (
          <CardAdFav
            {...favoriteActionProps}
            extendedTrackingData={extendedTrackingData}
            isDiscardedAdsEnabled={isDiscardedAdsEnabled}
            isMakedAsFavorite={isMakedAsFavorite}
          />
        )}
        {!isMobile && (
          <ActionsAdShare
            ad={ad}
            extendedTrackingData={extendedTrackingData}
            referer={referrer}
            size={actionAdShareButtonSizes.LARGE}
            trackingOrigin={actionAdShareTrackingOrigins.ADS_LIST_AD_SHARED}
            type={actionAdShareTypes.ICON}
          />
        )}
        {showCardMenu && (
          <AtomPopover
            content={({update}) => (
              <CardAdMenu
                {...priceDropActionProps}
                ad={ad}
                discardedAdsProps={discardedAdsProps}
                extendedTrackingData={extendedTrackingData}
                isDiscardedAdsEnabled={isDiscardedAdsEnabled}
                isFavorite={isMakedAsFavorite}
                updatePosition={update}
                trackingData={trackingData}
              />
            )}
            isVisible={isMenuOpen}
            onClose={handleCloseMenu}
            placement={atomPopoverPositions.BOTTOM_END}
            type="system"
          >
            <div>
              <CardAdMenuIcon onClick={handleClickMenu} />
            </div>
          </AtomPopover>
        )}
      </div>
    </div>
  )
}

CardAdInfoHeader.displayName = 'CardAdInfoHeader'

CardAdInfoHeader.propTypes = {
  ad: PropTypes.object.isRequired,
  cardAdFavProps: PropTypes.object,
  extendedTrackingData: PropTypes.object,
  hasMenu: PropTypes.bool,
  isDeleted: PropTypes.bool,
  isDiscardedAdsEnabled: PropTypes.bool,
  isPreview: PropTypes.bool,
  linkFactory: PropTypes.func,
  onAfterDiscard: PropTypes.func,
  showFavorite: PropTypes.bool,
  trackingData: PropTypes.object
}

export default CardAdInfoHeader
